import { LOOK_LABELS } from '@/looks';
import type { DesignThemeConfig } from '@/stores/design-preview-store';

/**
 * Field-level diff of a candidate design against the current live design, so the
 * sandbox can show an admin exactly what publishing will change. Pure and
 * dependency-light: compares only the editable theme_config fields and ignores
 * `generated` (it is derived from the colors on read). See DesignDiff for rendering.
 */

export type DesignChangeKind = 'color' | 'font' | 'template' | 'customizations' | 'scope';

export interface DesignChange {
  /** Stable key for React lists / tests. */
  key: string;
  /** Human label for the field (e.g. "Primary color"). */
  label: string;
  kind: DesignChangeKind;
  /** Live value (hex for colors, display text otherwise). Undefined = unset. */
  before?: string;
  /** Candidate value. Undefined = unset. */
  after?: string;
}

const norm = (v?: string): string | undefined =>
  typeof v === 'string' ? v.trim().toLowerCase() : undefined;

function templateLabel(presetId?: string): string | undefined {
  if (!presetId) return undefined;
  return LOOK_LABELS[presetId as keyof typeof LOOK_LABELS]?.label ?? presetId;
}

function overridesCount(look?: DesignThemeConfig['look']): number {
  const o = look?.overrides;
  return o ? Object.keys(o).length : 0;
}

/** Order-independent stringify of a flat string→string overrides map. */
function stableOverrides(look?: DesignThemeConfig['look']): string {
  const o = look?.overrides;
  if (!o) return '{}';
  return JSON.stringify(Object.fromEntries(Object.entries(o).sort(([a], [b]) => a.localeCompare(b))));
}

const COLOR_FIELDS: [keyof DesignThemeConfig, string][] = [
  ['primary_color', 'Primary color'],
  ['accent_color', 'Accent color'],
  ['bg_warm', 'Background'],
];

const FONT_FIELDS: [keyof DesignThemeConfig, string][] = [
  ['font_heading', 'Heading font'],
  ['font_body', 'Body font'],
];

/**
 * Returns one entry per field that differs between `live` and the design that
 * would be written (`next`). Publishing is a full REPLACE of theme_config, so a
 * field `live` has but `next` omits reverts to the build-time default on the
 * live site — that is a real change and IS reported (`after` undefined → shown
 * as "default"), not silently hidden. `palette_scope` is compared as a real
 * before/after with an unset value treated as 'base', so a base↔full flip is
 * reported in either direction (e.g. a revert from a full-palette publish back
 * to a pre-full backup). Callers should pass the config that will actually be
 * written (the sandbox stamps `palette_scope: 'full'` on publish), so the diff
 * reflects exactly what goes live.
 */
export function diffDesign(
  live: DesignThemeConfig | null | undefined,
  next: DesignThemeConfig | null | undefined,
): DesignChange[] {
  if (!next) return [];
  const changes: DesignChange[] = [];

  for (const [field, label] of COLOR_FIELDS) {
    const after = next[field] as string | undefined;
    const before = live?.[field] as string | undefined;
    if (norm(before) !== norm(after)) {
      changes.push({ key: field, label, kind: 'color', before, after });
    }
  }

  for (const [field, label] of FONT_FIELDS) {
    const after = next[field] as string | undefined;
    const before = live?.[field] as string | undefined;
    if (norm(before) !== norm(after)) {
      changes.push({ key: field, label, kind: 'font', before, after });
    }
  }

  const nextPreset = next.look?.preset_id;
  const livePreset = live?.look?.preset_id;
  if (norm(nextPreset) !== norm(livePreset)) {
    changes.push({
      key: 'template',
      label: 'Template',
      kind: 'template',
      before: templateLabel(livePreset),
      after: templateLabel(nextPreset),
    });
  } else if (stableOverrides(next.look) !== stableOverrides(live?.look)) {
    // Same template, but the per-knob customizations changed. (When the template
    // itself changes, that row already implies the overrides reset, so skip this.)
    const n = overridesCount(next.look);
    const p = overridesCount(live?.look);
    changes.push({
      key: 'customizations',
      label: 'Template customizations',
      kind: 'customizations',
      before: `${p} tweak${p === 1 ? '' : 's'}`,
      after: `${n} tweak${n === 1 ? '' : 's'}`,
    });
  }

  const liveScope = live?.palette_scope === 'full' ? 'full' : 'base';
  const nextScope = next.palette_scope === 'full' ? 'full' : 'base';
  if (liveScope !== nextScope) {
    changes.push({
      key: 'scope',
      label: 'Full-palette swap',
      kind: 'scope',
      before: liveScope === 'full' ? 'On' : 'Off',
      after: nextScope === 'full' ? 'On' : 'Off',
    });
  }

  return changes;
}
